import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useUserProfile } from '@/features/auth/api';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { NewPromptUploader } from './NewPromptUploader';
import { useTtsAccess } from '../../api';
import { usePromptUploads, useTtsTypes } from '@/features/voice-campaigns/api';
import { cn } from '@/lib/utils/cn';
import {
  AudioLines,
  ChevronDown,
  ChevronUp,
  FileAudio,
  Hash,
  Loader2,
  MicVocal,
  Pause,
  Play,
  Plus,
  Square,
  Trash2,
  Type,
  Variable as VariableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTtsPreview } from '../../hooks/useTtsPreview';
import type { TtsEditorMode, TtsFlowFormValues, TtsRow } from '../../types';

interface Props {
  row: TtsRow;
  index: number;
  totalRows: number;
  values: TtsFlowFormValues;
  /** Patch this single row in the values.ttsRows array. */
  onPatch: (patch: Partial<TtsRow>) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

const VARIABLE_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** Returns the editor-side mode for a given row. Wire-time only knows
 *  prompt | variable; the editor adds a synthetic `text` mode.
 *
 *  A brand-new text row carries `text: ''` (empty string). We test for the
 *  PRESENCE of a `text` field (`typeof row.text === 'string'`) rather than
 *  truthiness so a fresh empty text row still renders the textarea. */
function modeOf(row: TtsRow): TtsEditorMode {
  if (row.ttsType === 'variable') return 'variable';
  if (typeof row.text === 'string') return 'text';
  return 'prompt';
}

const MODE_VISUAL: Record<
  TtsEditorMode,
  { label: string; stripe: string; chipBg: string; chipFg: string; icon: typeof Play }
> = {
  prompt: {
    label: 'Prompt',
    stripe: 'bg-sky-500',
    chipBg: 'bg-sky-100',
    chipFg: 'text-sky-700',
    icon: FileAudio,
  },
  text: {
    label: 'Text',
    stripe: 'bg-fuchsia-500',
    chipBg: 'bg-fuchsia-100',
    chipFg: 'text-fuchsia-700',
    icon: Type,
  },
  variable: {
    label: 'Variable',
    stripe: 'bg-violet-500',
    chipBg: 'bg-violet-100',
    chipFg: 'text-violet-700',
    icon: VariableIcon,
  },
};

export function TtsStepCard({
  row,
  index,
  totalRows,
  values,
  onPatch,
  onRemove,
  onMoveUp,
  onMoveDown,
}: Props) {
  const prompts = usePromptUploads({ includeTtsFlow: true });
  const ttsTypes = useTtsTypes();
  const profile = useUserProfile();
  const ttsAccess = useTtsAccess();
  const role = useAuthStore((s) => s.role);
  const currentUserId = useAuthStore((s) => s.userId);
  // POST /promptupload is a ROLE_USER endpoint — only offer the shortcut
  // where the API will accept it.
  const canUploadPrompt = role === USER_ROLES.USER;
  const [uploadOpen, setUploadOpen] = useState(false);
  // Bumping this remounts the uploader, which wipes file / name / category.
  const [uploadKey, setUploadKey] = useState(0);
  const openUpload = () => {
    setUploadKey((k) => k + 1);
    setUploadOpen(true);
    // Uploading a new prompt replaces the existing selection.
    onPatch({
      value: '',
      text: undefined,
      previewUrl: undefined,
      previewTtsId: undefined,
    });
  };
  const closeUpload = () => {
    setUploadOpen(false);
    // Reset after the collapse animation so the panel doesn't blank mid-close.
    window.setTimeout(() => setUploadKey((k) => k + 1), 350);
  };

  const mode = modeOf(row);
  const visual = MODE_VISUAL[mode];

  /* ----- Shared playback (same hook the preview pane uses) -----
   * One row in → one step out. Prompts play their file, text rows
   * synthesize their text, variable rows synthesize a per-type sample.
   * All synth/cache/stop/toggle logic lives in the hook — including
   * stop-on-unmount when the row is removed or the modal closes. */
  const thisRow = useMemo(() => [row], [row]);
  const preview = useTtsPreview({
    rows: thisRow,
    language: values.language,
    gender: values.gender,
    // Cache synthesized previews onto THIS row so repeat plays are free.
    onPatchRow: (_i, patch) => onPatch(patch),
  });
  const step = preview.steps[0];
  const isPlaying = preview.playingIndex === 0 && preview.busy != null;
  const isSynth = preview.synthesizingIndex === 0;

  /* ----- Mode switching — every switch invalidates the cached preview ----- */
  const setMode = (next: TtsEditorMode) => {
    if (next === mode) return;
    if (next === 'prompt') {
      onPatch({
        ttsType: 'prompt',
        value: '',
        variableType: '',
        text: undefined,
        previewUrl: undefined,
        previewTtsId: undefined,
      });
    } else if (next === 'text') {
      onPatch({
        ttsType: 'prompt',
        value: '',
        variableType: '',
        text: row.text ?? '',
        previewUrl: undefined,
        previewTtsId: undefined,
      });
    } else {
      onPatch({
        ttsType: 'variable',
        value: '',
        variableType: '',
        text: undefined,
        previewUrl: undefined,
        previewTtsId: undefined,
      });
    }
  };

  /* ----- Prompt dropdown options (approved, TTS-relevant categories) -----
   * Prompts the user uploaded themselves that are still awaiting approval
   * (status 0) are listed too, tagged "pending approval" — they get
   * approved together with the TTS flow. */
  const promptOptions = useMemo(
    () =>
      (prompts.data ?? [])
        .filter((p) => {
          // The prompt already selected on this step is always listed, even
          // if it was uploaded under another category (noinput, thanks …),
          // so the dropdown can display it.
          const inCategory =
            String(p.promptId) === row.value ||
            p.promptCategory === 'welcome' ||
            p.promptCategory === 'menu' ||
            p.promptCategory === 'tts';
          if (!inCategory) return false;
          const approved =
            p.promptStatus === undefined ||
            Number(p.promptStatus) === 1 ||
            p.promptStatus === '1';
          const ownPending =
            p.promptStatus !== undefined &&
            Number(p.promptStatus) === 0 &&
            p.userId != null &&
            Number(p.userId) === Number(currentUserId);
          return approved || ownPending;
        })
        .map((p) => {
          const pending = p.promptStatus !== undefined && Number(p.promptStatus) === 0;
          const base = p.fileName || p.promptName || `#${p.promptId}`;
          return {
            id: String(p.promptId),
            label: pending ? `${base} (pending approval)` : base,
          };
        }),
    [prompts.data, currentUserId, row.value],
  );

  /* ----- Variable slots — cap by user limit, exclude slots used by
   *  OTHER variable rows ----- */
  const totalVariable = Number(
    (profile.data as { totalVariable?: number } | undefined)?.totalVariable ?? 0,
  );
  const slotOptions = useMemo(() => {
    const used = new Set(
      values.ttsRows
        .filter((r, i) => i !== index && r.ttsType === 'variable' && r.value !== '')
        .map((r) => Number(r.value)),
    );
    const cap = totalVariable > 0 ? totalVariable : 10;
    return VARIABLE_SLOTS.filter((s) => s <= cap && !used.has(s));
  }, [values.ttsRows, index, totalVariable]);

  return (
    <div className="group/step relative flex gap-2 rounded-lg border border-border bg-card shadow-sm transition-all hover:shadow-md">
      {/* Step number rail + drag handle */}
      <div className="flex flex-col items-center justify-between border-r border-border bg-muted/30 px-2 py-2">
        <span
          className={cn(
            'inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold uppercase text-white',
            visual.stripe,
          )}
        >
          {index + 1}
        </span>
        <div className="flex flex-col gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5 text-muted-foreground"
            onClick={onMoveUp}
            disabled={!onMoveUp || index === 0}
            aria-label="Move up"
          >
            <ChevronUp className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5 text-muted-foreground"
            onClick={onMoveDown}
            disabled={!onMoveDown || index === totalRows - 1}
            aria-label="Move down"
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Card body */}
      <div className="flex flex-1 flex-col gap-2 py-2 pr-2">
        {/* Mode segmented control + preview + remove */}
        <div className="flex items-start gap-2">
          <ModeSegmented mode={mode} onChange={setMode} />
          <div className="flex-1" />
          <Button
            type="button"
            size="icon"
            variant="outline"
            className={cn(
              'h-7 w-7 shrink-0 rounded-full',
              isPlaying && 'border-primary text-primary',
            )}
            onClick={() => step && preview.handlePlayStep(step)}
            disabled={!step || !step.playable || isSynth}
            aria-label={isPlaying ? 'Stop preview' : 'Preview'}
            title={
              isPlaying
                ? 'Stop'
                : step?.playable
                  ? 'Preview this step'
                  : mode === 'variable'
                    ? 'Pick a type first'
                    : 'Nothing to preview yet'
            }
          >
            {isSynth ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : isPlaying ? (
              <Square className="h-3.5 w-3.5" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
            onClick={onRemove}
            aria-label="Remove step"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Mode-specific body */}
        {mode === 'prompt' && (
          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground">Pick from voice library</Label>
            <div className="flex items-center gap-2">
            <Select
              value={row.value}
              // While the "New prompt" panel is open the user either uploads
              // a new prompt or picks an existing one — never both.
              disabled={uploadOpen}
              onValueChange={(v) =>
                onPatch({
                  value: v,
                  text: undefined,
                  previewUrl: undefined,
                  previewTtsId: undefined,
                })
              }
            >
              <SelectTrigger className="h-8 flex-1 text-xs">
                <SelectValue
                  placeholder={
                    prompts.isLoading
                      ? 'Loading…'
                      : promptOptions.length === 0
                        ? 'No prompts uploaded'
                        : 'Pick a prompt'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {promptOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canUploadPrompt && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(
                  'h-8 shrink-0 gap-1 border-sky-300 px-2.5 text-xs font-medium text-sky-700 hover:bg-sky-50 hover:text-sky-800 dark:border-sky-800 dark:text-sky-300 dark:hover:bg-sky-950/40',
                  uploadOpen && 'bg-sky-50 dark:bg-sky-950/40',
                )}
                onClick={() => (uploadOpen ? closeUpload() : openUpload())}
                aria-expanded={uploadOpen}
              >
                <Plus
                  className={cn(
                    'h-3.5 w-3.5 transition-transform duration-200',
                    uploadOpen && 'rotate-45',
                  )}
                />
                New prompt
              </Button>
            )}
            </div>
            {canUploadPrompt && (
              <div
                className={cn(
                  'grid transition-[grid-template-rows,opacity,visibility] duration-300 ease-out',
                  uploadOpen
                    ? 'visible grid-rows-[1fr] opacity-100'
                    : 'invisible grid-rows-[0fr] opacity-0',
                )}
                aria-hidden={!uploadOpen}
              >
                <div className="overflow-hidden">
                  <div className="pt-1.5">
                    <NewPromptUploader
                      key={uploadKey}
                      onClose={closeUpload}
                      onUploaded={({ promptId }) => {
                        if (!promptId) return;
                        // Select the freshly uploaded (pending) prompt.
                        onPatch({
                          value: promptId,
                          text: undefined,
                          previewUrl: undefined,
                          previewTtsId: undefined,
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {mode === 'text' && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] text-muted-foreground">
                Type what the bot should say
              </Label>
              <span
                className={cn(
                  'font-mono text-[11px]',
                  (row.text ?? '').length > 1000 ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                {(row.text ?? '').length} / 1000
              </span>
            </div>
            <Textarea
              rows={2}
              maxLength={1020}
              value={row.text ?? ''}
              readOnly={ttsAccess.blocked}
              onFocus={() => {
                if (ttsAccess.blocked) {
                  toast.error(ttsAccess.message ?? 'TTS balance low, contact your manager.', {
                    id: 'tts-blocked',
                  });
                }
              }}
              onChange={(e) =>
                onPatch({
                  text: e.target.value,
                  // Invalidate the cached preview when text changes.
                  previewUrl: undefined,
                  previewTtsId: undefined,
                })
              }
              placeholder="e.g. Press 1 for sales, 2 for support."
              className="resize-none text-sm"
            />
            <p className="text-[10px] text-muted-foreground">
              Auto-synthesized into a prompt when you save the flow. Click ▶ to preview without
              saving.
            </p>
          </div>
        )}

        {mode === 'variable' && (
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Slot</Label>
              <Select value={row.value} onValueChange={(v) => onPatch({ value: v })}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Pick slot" />
                </SelectTrigger>
                <SelectContent>
                  {slotOptions.map((s) => (
                    <SelectItem key={s} value={String(s)}>
                      Slot {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Type</Label>
              <Select
                value={row.variableType}
                onValueChange={(v) =>
                  onPatch({
                    variableType: v,
                    // Cache invalidation — REQUIRED so switching type
                    // re-synthesizes the NEW type's sample instead of
                    // replaying the old one (the "always plays number" bug).
                    previewUrl: undefined,
                    previewTtsId: undefined,
                  })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Pick type" />
                </SelectTrigger>
                <SelectContent>
                  {(ttsTypes.data ?? []).map((t, i) => (
                    <SelectItem key={`${t.tts_type}-${i}`} value={t.tts_type}>
                      {t.tts_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Mode chip (top-right floating) */}
      {/* <span
        aria-hidden
        className={cn(
          'absolute right-2 top-2 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide opacity-0 transition-opacity group-hover/step:opacity-100',
          visual.chipBg,
          visual.chipFg,
        )}
      >
        <visual.icon className="h-2.5 w-2.5" />
        {visual.label}
      </span> */}

      {/* Silence unused import warnings — these icons are reserved for the
          right-pane preview that renders alongside this card. */}
      <span aria-hidden className="hidden">
        <AudioLines />
        <Hash />
        <MicVocal />
        <Pause />
      </span>
    </div>
  );
}

function ModeSegmented({
  mode,
  onChange,
}: {
  mode: TtsEditorMode;
  onChange: (m: TtsEditorMode) => void;
}) {
  const OPTIONS: { id: TtsEditorMode; label: string }[] = [
    { id: 'prompt', label: 'Prompt' },
    { id: 'text', label: 'Text' },
    { id: 'variable', label: 'Variable' },
  ];
  return (
    <div className="inline-flex rounded-md border border-border bg-muted/30 p-0.5">
      {OPTIONS.map((opt) => {
        const active = mode === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={cn(
              'rounded px-2 py-0.5 text-[11px] font-medium transition-colors',
              active
                ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}